/**
 * Typed wrappers around the `/api/sessions/dashboard/*` endpoints.
 * The shapes here mirror the server repo's return types one-for-one so
 * the dashboard page stays unaware of the SQL underneath.
 *
 * Mount path is `/api/sessions` (not `/api/analytics`) because every
 * common ad-blocker filter list silently drops requests with
 * `analytics` in the URL — see the matching note in
 * `src/server/routes/analytics.ts`. The dashboard endpoints are
 * admin-only, but they live under the same parent so future-you doesn't
 * have to reason about a split URL surface.
 */

import { authFetchJson } from '../api';
import type { AnalyticsEventType } from '@shared/analytics';
import type { GameMode } from '@shared/chart';
import type { Variant } from '@shared/gameTypes';

export interface DashboardKpis {
  overall: {
    songStarts: number;
    songEnds: number;
    songCompletions: number;
    songAbandons: number;
    completionRate: number;
    startCompletionRate: number;
    distinctPlayers: number;
    sessions: number;
  };
  byMode: ReadonlyArray<{
    mode: GameMode;
    songStarts: number;
    songEnds: number;
    songCompletions: number;
    songAbandons: number;
    completionRate: number;
    startCompletionRate: number;
    avgProgressTier: number | null;
    distinctPlayers: number;
    /** Total time played in this mode (sum of run durations across ended + abandoned runs). Null when no run carried a duration. */
    totalDurationMs: number | null;
  }>;
}

export interface DashboardProgressBucket {
  tier: number;
  count: number;
}

export interface DashboardPerMapRow {
  mode: GameMode;
  mapSlug: string;
  variant: Variant;
  difficulty: number;
  title: string | null;
  songStarts: number;
  songEnds: number;
  songCompletions: number;
  completionRate: number;
  avgProgressTier: number | null;
  p10ProgressTier: number | null;
  p50ProgressTier: number | null;
  p90ProgressTier: number | null;
  avgDurationMs: number | null;
  /** Total time played on this song (sum of run durations across ended + abandoned runs). Null when no run carried a duration. */
  totalDurationMs: number | null;
  /** Count at each progress tier (index 0..6) across song_end + song_abandon events. */
  tierCounts: number[];
}

export interface DashboardDauRow {
  date: string;
  dau: number;
  newUsers: number;
  sessions: number;
}

/** Bucket granularity for the playtime time-series. */
export type PlaytimeBucketUnit = 'hour' | 'day';

export interface DashboardPlaytimeBucket {
  /** Bucket start as an ISO 8601 UTC timestamp (`2026-06-01T00:00:00Z`). */
  bucket: string;
  /** Total playtime in this bucket across every mode (ms). */
  totalMs: number;
  /** Per-mode playtime (ms) within this bucket. Absent modes are zero. */
  byMode: Partial<Record<GameMode, number>>;
}

export interface DashboardPlaytimeSeries {
  /** Granularity of the returned buckets — echoes the requested bucket. */
  bucket: PlaytimeBucketUnit;
  series: DashboardPlaytimeBucket[];
}

export interface DashboardRetentionRow {
  cohortDate: string;
  cohortSize: number;
  d1Retained: number;
  d7Retained: number;
  d1Rate: number;
  d7Rate: number;
}

export interface DashboardRecentEvent {
  id: string;
  eventId: string;
  eventType: string;
  userUid: string | null;
  isAnonymous: boolean;
  sessionId: string | null;
  occurredAt: string;
  receivedAt: string;
  mode: GameMode | null;
  mapId: string | null;
  mapSlug: string | null;
  difficulty: number | null;
  variant: Variant | null;
  progressTier: number | null;
  completed: boolean | null;
  durationMs: number | null;
  props: Record<string, string | number | boolean | null> | null;
  displayName: string | null;
  email: string | null;
}

export interface DashboardActiveUser {
  userUid: string;
  displayName: string | null;
  email: string | null;
  isAnonymous: boolean;
  /** Most recent event timestamp (ISO 8601). */
  lastSeenAt: string;
  eventCount: number;
  hasPlayed: boolean;
}

export interface WindowOpts {
  since?: Date;
  until?: Date;
}

function windowQuery(opts: WindowOpts): string {
  const params = new URLSearchParams();
  if (opts.since) {
    params.set('since', opts.since.toISOString());
  }
  if (opts.until) {
    params.set('until', opts.until.toISOString());
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export async function fetchDashboardKpis(opts: WindowOpts = {}): Promise<DashboardKpis> {
  return authFetchJson<DashboardKpis>(`/api/sessions/dashboard/kpis${windowQuery(opts)}`);
}

export async function fetchProgressDistribution(
  opts: WindowOpts & { mode?: GameMode } = {},
): Promise<{ buckets: DashboardProgressBucket[] }> {
  const params = new URLSearchParams();
  if (opts.since) {
    params.set('since', opts.since.toISOString());
  }
  if (opts.until) {
    params.set('until', opts.until.toISOString());
  }
  if (opts.mode) {
    params.set('mode', opts.mode);
  }
  const qs = params.toString();
  return authFetchJson(`/api/sessions/dashboard/progress-distribution${qs ? `?${qs}` : ''}`);
}

export async function fetchPerMapStats(
  opts: WindowOpts = {},
): Promise<{ rows: DashboardPerMapRow[] }> {
  return authFetchJson(`/api/sessions/dashboard/per-map${windowQuery(opts)}`);
}

export async function fetchDailyActiveUsers(days = 30): Promise<{ series: DashboardDauRow[] }> {
  return authFetchJson(`/api/sessions/dashboard/dau?days=${days}`);
}

export async function fetchPlaytimeSeries(
  opts: WindowOpts & { bucket: PlaytimeBucketUnit },
): Promise<DashboardPlaytimeSeries> {
  const params = new URLSearchParams();
  if (opts.since) {
    params.set('since', opts.since.toISOString());
  }
  if (opts.until) {
    params.set('until', opts.until.toISOString());
  }
  params.set('bucket', opts.bucket);
  return authFetchJson(`/api/sessions/dashboard/playtime-series?${params.toString()}`);
}

export async function fetchRetentionCohort(
  days = 30,
): Promise<{ cohorts: DashboardRetentionRow[] }> {
  return authFetchJson(`/api/sessions/dashboard/retention?days=${days}`);
}

export interface RecentEventsOpts {
  limit?: number;
  userUid?: string;
  eventTypes?: ReadonlyArray<AnalyticsEventType>;
}

export async function fetchRecentEvents(
  opts: RecentEventsOpts = {},
): Promise<{ events: DashboardRecentEvent[] }> {
  const params = new URLSearchParams();
  params.set('limit', String(opts.limit ?? 50));
  if (opts.userUid) {
    params.set('userUid', opts.userUid);
  }
  if (opts.eventTypes && opts.eventTypes.length > 0) {
    params.set('eventTypes', opts.eventTypes.join(','));
  }
  return authFetchJson(`/api/sessions/dashboard/recent-events?${params.toString()}`);
}

export async function fetchActiveUsers(
  limit = 100,
): Promise<{ users: DashboardActiveUser[] }> {
  return authFetchJson(`/api/sessions/dashboard/active-users?limit=${limit}`);
}
